<template>
    <modal v-bind:show.sync="show" ref="modal" size="medium">
        <div slot="header"></div>

        <form-wizard
            v-cloak
            ref="wizard"
            v-bind:validation="validators"
            v-bind:action="actionUrl"
        >
            <wizard-step
                v-bind:next-label="trans('Next: Naming')"
            >
                <div class="row">
                    <div class="column">
                        <h4 class="add-product-heading f-s-7 m-b-2">{{ trans('What type of product would you like to create?') }}</h4>
                        <p class="f-s-4 c-medium-tint m-b-6">{{ trans('Choose the product type that best matches what you want to build.') }}</p>
                    </div>
                </div>
                <product-type-selector
                    v-bind:product-types="productTypes"
                    v-bind:selected-type="selectedType"
                    v-bind:items-per-row="4"
                    name="product_type"
                    v-on:selected="selectType"
                ></product-type-selector>
            </wizard-step>
            <wizard-step
                v-bind:prev-label="trans('Back to Product Type')"
                v-bind:next-label="trans('Create Product')"
                v-on:enter="elementFocus('title')"
            >
                <div class="row">
                    <div class="column">
                        <h4 class="add-product-heading f-s-7 m-b-2">{{ trans('Product Naming & Organization') }}</h4>
                        <p class="f-s-4 c-medium-tint m-b-6">{{ trans("Your product name will be used throughout your membership site. Use a name you want members to see, you can change it later.") }}</p>
                    </div>
                </div>
                <div class="row m-b-2">
                    <div class="small-2 columns">
                        <icon-picker
                            name="icon"
                            v-bind:show-clear-button="false"
                            v-bind:value="selectedEmoji"
                        ></icon-picker>
                    </div>
                    <div class="small-10 columns">
                        <div v-bind:class="{'has-error': errors.has('title')}" class="form-group">
                            <input v-model="title" class="input__field" type="text" ref="title" name="title" v-bind:placeholder="trans('Product name')" maxlength="80">
                        </div>
                        <div v-if="errors.has('title')" class="form-help">{{ errors.get('title') }}</div>
                    </div>
                </div>
                <div class="row">
                    <div class="column">
                        <h5 class="f-s-4 m-b-2">{{ trans('Sidebar Heading') }}</h5>
                        <p class="f-s-4 c-medium-tint m-b-2">{{ trans("Group this product under a heading in your members' area sidebar (optional).") }}</p>
                    </div>
                </div>
                <div class="row">
                    <div class="small-12 columns">
                        <div v-bind:class="{'has-error': errors.has('category_id')}" class="form-group hl-txt">
                            <select class="input__field" ref="category_id" name="product_category_id" v-model="productCategoryId">
                                <option value="NEW">{{ trans('Create new category') }}</option>
                                <option disabled>---</option>
                                <option v-for="category in productCategories" v-bind:key="category.encoded_id" v-bind:value="category.encoded_id">{{ category.title }}</option>
                            </select>
                        </div>
                        <div v-if="errors.has('category_id')" class="form-help">{{ errors.get('category_id') }}</div>
                        <div v-if="productCategoryId && productCategoryId === 'NEW'" v-bind:class="{'has-error': errors.has('category_title')}" class="form-group">
                            <input v-model="productCategoryTitle" class="input__field" type="text" ref="product_category_title" name="product_category_title" v-bind:placeholder="trans('Sidebar group title')" maxlength="255" required>
                        </div>
                        <div v-if="errors.has('category_title')" class="form-help">{{ errors.get('category_title') }}</div>
                    </div>
                </div>
            </wizard-step>
        </form-wizard>

        <div slot="footer"></div>
    </modal>
</template>
<script>
import { EventBus } from '../../event-bus.js';
import Errors from '../../errors.js';
import Modal from '../modals/Modal.vue';
import ProductTypeSelector from './ProductTypeSelector.vue';

export default {
    components: {
        Modal,
        ProductTypeSelector,
    },
    props: {
        productCategories: {
            type: Array,
        },
        productTypes: {
            type: Array,
        },
    },
    data() {
        return {
            show: false,
            errors: new Errors(),
            validators: {
                2: this.validateTitle && this.validateCategory,
            },
            title: null,
            productCategoryId: this.productCategories[0].encoded_id, // There will always be at least one category because we create "General" by default
            productCategoryTitle: null,
            actionUrl: window.Kourses.activeBaseUrl + '/products',
            selectedType: 'course',
            selectedEmoji: this.getEmojiForType('course'),
        }
    },
    watch: {
        productCategoryId() {
            this.errors.clear();
        },
        show(show) {
            if (show === false) {
                this.reset();
            }
        },
        selectedType(newType) {
            this.selectedEmoji = this.getEmojiForType(newType);
        },
    },
    methods: {
        getEmojiForType(type) {
            let typeObj = this.productTypes.find(t => t.type === type);

            return typeObj ? typeObj.default_emoji : {'type': 'emoji', 'value': '🎓'};
        },
        reset() {
            this.title = null;
            this.productCategoryId = this.productCategories[0].encoded_id;
            this.productCategoryTitle = null;
            this.selectedType = 'course';
            this.errors.clear();
            this.$refs.wizard.reset();
        },
        validateTitle() {
            this.errors.clear();

            if ( ! this.title || this.title.trim().length === 0) {
                this.errors.set('title', this.trans('Please enter a name for your product'));

                return false;
            }

            return true;
        },
        validateCategory() {
            this.errors.clear();

            if ( ! this.productCategoryId) {
                this.errors.set('category_id', this.trans('Please select existing category or create a new one'));

                return false;
            }

            if (this.productCategoryId === 'NEW' && (! this.productCategoryTitle || this.productCategoryTitle.trim().length === 0)) {
                this.errors.set('category_title', this.trans('Please enter a title for your new category'));

                return false;
            }

            return true;
        },
        elementFocus(element) {
            this.$nextTick(() => this.$refs[element].focus());
        },
        selectType(type) {
            this.selectedType = type;
        },
    },
    created() {
        let $vm = this;

        EventBus.$on('modal:new-product', () => {
            $vm.show = true;
        });
    },
    mounted() {
        // Auto-open (show) modal window if hash is what we want
        if (window.location.hash && window.location.hash.substring(1) === 'new-product') {
            this.show = true;
        }
    },
}
</script>